import { useEffect, useRef, useState } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useAuthStore } from '@/stores/authStore'
import { apiService } from '@/services/apiService'
import { useUnreadMessageCount } from '@/hooks/useMessages'
import { usePendingRequestCount } from '@/hooks/useCottageRequests'
import {
  LogOut,
  Menu,
  X,
  Bell,
  Waves,
  Search,
  ChevronDown,
  Home,
  Calendar,
  CalendarHeart,
  MessageSquare,
  MessageCircle,
  Camera,
  Users,
  FileText,
  Shield,
  Sun,
  Landmark,
  Archive,
  Church,
  Sparkles,
  ShoppingBag,
  type LucideIcon,
} from 'lucide-react'
import CommandPalette from '@/components/ui/CommandPalette'
import NotificationPanel from '@/components/ui/NotificationPanel'
import ShorewoodSeal from '@/components/ui/ShorewoodSeal'
import { isInResidence } from '@/hooks/useDirectory'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Restrict to BOARD/ADMIN. */
  requiresAdmin?: boolean
}

// Top-level member destinations. Secondary pages live under "More".
const PRIMARY_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/bulletin', label: 'Bulletin', icon: MessageSquare },
  { to: '/help', label: 'Help Desk', icon: Sparkles },
  { to: '/directory', label: 'Directory', icon: Users },
  { to: '/shop', label: 'Shop', icon: ShoppingBag },
]

const MORE_NAV: NavItem[] = [
  { to: '/album', label: 'Album', icon: Camera },
  { to: '/messages', label: 'Messages', icon: MessageCircle },
  { to: '/events', label: 'Events', icon: CalendarHeart },
  { to: '/board', label: 'Board', icon: Landmark },
  { to: '/documents', label: 'Documents', icon: FileText },
  { to: '/chapel', label: 'Lake Shore Chapel', icon: Church },
  { to: '/archives', label: 'The Shorewood Archives', icon: Archive },
  { to: '/admin', label: 'Admin', icon: Shield, requiresAdmin: true },
]

// Stable identifiers consumed by <TutorialTour />. Keep in sync with the
// step definitions in components/onboarding/TutorialTour.tsx.
const TOUR_NAV_BY_PATH: Record<string, string | undefined> = {
  '/dashboard': 'nav-dashboard',
  '/calendar': 'nav-calendar',
  '/bulletin': 'nav-bulletin',
  '/directory': 'nav-directory',
}

const Header = () => {
  const { user, signOut } = useAuthStore()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'BOARD'
  const here = isInResidence(user?.inResidenceUntil)

  // Badge count for the Admin entry — gated to UserRole.ADMIN inside the hook
  // (BOARD members see the Admin link but cannot decide requests, so no
  // badge for them).
  const { count: adminPendingCount } = usePendingRequestCount()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)

  // Close menus on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false)
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Cmd+K / Ctrl+K opens the palette anywhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const handleLogout = async () => {
    setProfileOpen(false)
    setMobileOpen(false)
    try {
      await signOut()
    } finally {
      navigate('/login')
    }
  }

  const moreItems = MORE_NAV.filter((it) => !it.requiresAdmin || isAdmin)
  const allNav = [...PRIMARY_NAV, ...moreItems]

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-paper/85 border-b border-mist/50">
        {/* Heritage strip — restrained tagline */}
        <div className="hidden md:block bg-deepwater text-white/85">
          <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between text-[0.7rem]">
            <span className="inline-flex items-center gap-2 uppercase tracking-eyebrow text-gold/90">
              <Waves className="w-3 h-3" aria-hidden="true" />
              The Shorewood Association · Members Since 1902
            </span>
            <span className="font-serif italic text-white/60">
              Lake Shore Chapel opens Memorial Day weekend
            </span>
          </div>
        </div>

        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-[72px] gap-3">
            {/* Brand mark */}
            <Link to="/dashboard" className="flex items-center gap-3 group shrink-0">
              <ShorewoodSeal className="w-12 h-12 shrink-0" />
              <div className="leading-tight">
                <div className="font-serif text-lg font-bold text-deepwater group-hover:text-primary transition-colors">
                  Shorewood
                </div>
                <div className="font-sans text-[0.65rem] uppercase tracking-eyebrow text-slate -mt-0.5">
                  Member Portal
                </div>
              </div>
            </Link>

            {/* Desktop primary nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              {PRIMARY_NAV.map((link) => (
                <NavPill
                  key={link.to}
                  to={link.to}
                  label={link.label}
                  dataTour={TOUR_NAV_BY_PATH[link.to]}
                />
              ))}
              {/* More dropdown */}
              {moreItems.length > 0 && (
                <div className="relative" ref={moreRef}>
                  <button
                    type="button"
                    onClick={() => setMoreOpen((v) => !v)}
                    className={`relative inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
                      moreOpen ? 'text-primary bg-mist/30' : 'text-ink/75 hover:text-primary hover:bg-mist/30'
                    }`}
                    aria-expanded={moreOpen}
                  >
                    More
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {moreOpen && (
                    <div className="absolute top-full mt-2 right-0 w-64 bg-paper rounded-2xl shadow-float border border-mist/40 overflow-hidden animate-fade-in origin-top-right">
                      <div className="p-1.5">
                        {moreItems.map((it) => {
                          const Icon = it.icon
                          const badge = it.to === '/admin' && adminPendingCount > 0 ? adminPendingCount : 0
                          return (
                            <NavLink
                              key={it.to}
                              to={it.to}
                              onClick={() => setMoreOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                                  isActive
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-deepwater hover:bg-mist/30'
                                }`
                              }
                            >
                              <div className="w-8 h-8 rounded-lg bg-mist/40 flex items-center justify-center shrink-0">
                                <Icon className="w-4 h-4 text-deepwater" />
                              </div>
                              <span className="text-sm font-medium flex-1">{it.label}</span>
                              {badge > 0 && (
                                <span
                                  className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-copper text-white text-[0.55rem] font-bold"
                                  aria-label={`${badge} pending`}
                                >
                                  {badge > 99 ? '99+' : badge}
                                </span>
                              )}
                            </NavLink>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right cluster: search, bell, profile */}
            <div className="flex items-center gap-1.5">
              {/* Command palette trigger */}
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="hidden md:inline-flex items-center gap-2 px-3 h-10 rounded-full bg-white border border-mist/60 hover:border-primary/40 hover:bg-white shadow-paper text-slate hover:text-deepwater transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                aria-label="Search and quick navigate"
              >
                <Search className="w-3.5 h-3.5" />
                <span className="text-xs lg:hidden xl:inline">Search</span>
                <kbd className="hidden xl:inline-flex items-center gap-0.5 ml-1 text-[0.6rem] uppercase tracking-eyebrow font-semibold text-slate bg-mist/40 px-1.5 py-0.5 rounded">
                  ⌘K
                </kbd>
              </button>

              {/* Messages quick-link (visible at all sizes so mobile users
                  can reach DMs without diving into the More menu). */}
              <MessagesButton />

              {/* Notification bell */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setNotifOpen((v) => !v)}
                  className="hidden md:inline-flex relative w-10 h-10 items-center justify-center rounded-full text-slate hover:bg-mist/40 hover:text-deepwater transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                  aria-label="Notifications"
                  aria-expanded={notifOpen}
                >
                  <Bell className="w-4 h-4" />
                  <UnreadDot />
                </button>
                <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
              </div>

              {/* Profile pill */}
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen((v) => !v)}
                  data-tour="header-profile"
                  className="hidden sm:flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full border border-mist/60 bg-white/60 hover:border-primary/40 hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                  aria-expanded={profileOpen}
                  aria-label="Account menu"
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-water-gradient flex items-center justify-center text-white text-[0.7rem] font-semibold ring-2 ring-paper">
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <>
                          {(user?.firstName?.[0] ?? 'M') + (user?.lastName?.[0] ?? '')}
                        </>
                      )}
                    </div>
                    {here && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-meadow ring-2 ring-paper"
                        title="At the lake"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="leading-tight pr-0.5 max-w-[10rem]">
                    <div className="font-sans text-xs font-medium text-deepwater truncate">
                      {user?.firstName ?? 'Member'} {user?.lastName?.[0] ?? ''}
                      {user?.lastName ? '.' : ''}
                    </div>
                    <div className="text-[0.6rem] text-slate uppercase tracking-eyebrow truncate">
                      {here ? 'At the lake' : user?.role?.toLowerCase() ?? 'member'}
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-3 h-3 text-slate transition-transform ${profileOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-paper rounded-2xl shadow-float border border-mist/40 overflow-hidden animate-fade-in origin-top-right">
                    <div className="p-4 border-b border-mist/40">
                      <div className="font-serif text-sm font-semibold text-deepwater">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="text-xs text-slate truncate">{user?.email}</div>
                      {here && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-meadow/15 text-meadow px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-eyebrow">
                          <Sun className="w-2.5 h-2.5" />
                          At the lake
                        </div>
                      )}
                    </div>
                    <div className="p-1.5">
                      <Link
                        to="/profile"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-deepwater hover:bg-mist/30 transition-colors"
                      >
                        Profile & settings
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-deepwater hover:bg-mist/30 transition-colors"
                        >
                          <Shield className="w-3.5 h-3.5" />
                          Board tools
                        </Link>
                      )}
                    </div>
                    <div className="p-1.5 border-t border-mist/40">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-copper hover:bg-copper/10 transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile menu */}
              <button
                type="button"
                className="lg:hidden inline-flex w-10 h-10 items-center justify-center rounded-full text-deepwater hover:bg-mist/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                onClick={() => setMobileOpen((v) => !v)}
                data-tour="mobile-menu-trigger"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile drawer */}
          {mobileOpen && (
            <div className="lg:hidden border-t border-mist/50 py-3 animate-fade-in">
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false)
                  setPaletteOpen(true)
                }}
                className="w-full mb-3 inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-mist/60 text-slate"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Search the portal</span>
              </button>

              <div className="grid grid-cols-2 gap-1.5">
                {allNav.map((it) => {
                  const Icon = it.icon
                  const badge = it.to === '/admin' && adminPendingCount > 0 ? adminPendingCount : 0
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-white/50 text-deepwater hover:bg-mist/30'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1">{it.label}</span>
                      {badge > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-copper text-white text-[0.55rem] font-bold"
                          aria-label={`${badge} pending`}
                        >
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </NavLink>
                  )
                })}
              </div>

              <div className="mt-3 pt-3 border-t border-mist/40 flex items-center justify-between gap-3">
                <Link
                  to="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-sm text-deepwater"
                >
                  <div className="w-8 h-8 rounded-full bg-water-gradient flex items-center justify-center text-white text-[0.65rem] font-semibold">
                    {(user?.firstName?.[0] ?? 'M') + (user?.lastName?.[0] ?? '')}
                  </div>
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-copper border border-copper/40"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  )
}

const NavPill = ({
  to,
  label,
  dataTour,
}: {
  to: string
  label: string
  dataTour?: string
}) => (
  <NavLink
    to={to}
    data-tour={dataTour}
    className={({ isActive }) =>
      `relative px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
        isActive ? 'text-primary' : 'text-ink/75 hover:text-primary hover:bg-mist/30'
      }`
    }
  >
    {({ isActive }) => (
      <>
        {label}
        {isActive && (
          <span
            className="absolute -bottom-0.5 left-3 right-3 h-0.5 rounded-full bg-gradient-to-r from-gold/40 via-gold to-gold/40"
            aria-hidden="true"
          />
        )}
      </>
    )}
  </NavLink>
)

/**
 * Direct-messages quick link with a live unread badge. Always visible so
 * mobile users have a one-tap entry point — the desktop "More" menu and
 * the directory "Message" buttons remain the other paths.
 */
const MessagesButton = () => {
  const { data: unread = 0 } = useUnreadMessageCount()
  return (
    <NavLink
      to="/messages"
      className={({ isActive }) =>
        `relative inline-flex w-10 h-10 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-slate hover:bg-mist/40 hover:text-deepwater'
        }`
      }
      aria-label={unread > 0 ? `Messages (${unread} unread)` : 'Messages'}
    >
      <MessageCircle className="w-4 h-4" />
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-copper text-white text-[0.55rem] font-bold ring-2 ring-paper inline-flex items-center justify-center"
          aria-hidden="true"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </NavLink>
  )
}

/**
 * Tiny live unread dot fed by the notifications query cache. Renders only
 * when there's at least one unread item.
 */
const UnreadDot = () => {
  const { data: list = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiService.getNotifications(),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  })
  const unread = list.filter((n) => !n.isRead).length
  if (unread === 0) return null
  return (
    <span
      className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-copper ring-2 ring-paper"
      aria-hidden="true"
    />
  )
}

export default Header
